import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { Icon } from '@creditornot/alchemy-react';
import * as icons from '@creditornot/alchemy-react/icons';
import { borderWidth, cornerRadius } from '@creditornot/alchemy-react/tokens';
import { TextButton } from '@creditornot/cb-button';
import { Pagination } from '@creditornot/cb-data-table';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Popover } from '@creditornot/cb-popover';
import { Tag } from '@creditornot/cb-tag';
import { ApiErrorNotification } from 'common/components/cookbook/ApiErrorNotification';
import { DataTable } from 'common/components/cookbook/DataTable/DataTable';
import { PopoverContent } from 'common/components/cookbook/PopoverContent';
import { ProductCard } from 'common/components/cookbook/ProductCard/ProductCard';
import { SearchInput } from 'common/components/cookbook/Search/SearchInput';
import { Stack } from 'common/components/cookbook/Stack';
import { Typography as T } from 'common/components/cookbook/Typography/Typography';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountry } from 'common/hooks/useSelectedCountry';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { useUserLocale } from 'common/hooks/useUserLocale';
import { useVenuesQuery } from 'common/hooks/useVenuesQuery';
import { uuidv4 } from 'common/utils/uuidv4';

import { OverridesSummary } from '../api-types';
import { useListOverridesQuery } from '../query';
import { overrideManagementRouteApi } from '../routeApi';
import { OverridesFilter } from './OverridesFilter';
import { OverrideSummaryDetailsModal } from './OverrideSummaryDetailsModal';
import { StatusPopoverContentSection } from './StatusPopoverContentSection';
import {
  getEndedOverrides,
  getImpactingOverrides,
  getTagIcon,
  getTagVariant,
  getUpcomingOverrides,
} from './utils';

const Controls = styled(Stack)`
  padding-right: 12px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${color.border};
`;

const StyledSearchInput = styled(SearchInput)`
  width: 240px;
  height: 24px;
`;

const AllProductsThumbnail = styled.div`
  width: 40px;
  height: 40px;
  border: ${borderWidth.thin} solid ${color.border};
  border-radius: ${cornerRadius.medium};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RightContainer = 'div';

export default function Overrides() {
  const format = useFormatMessage();
  const countryCodes = useSelectedCountryCodes();
  const locale = useUserLocale();
  const countryDefaultLang = useSelectedCountry().data?.defaultLanguageCode;

  const navigate = overrideManagementRouteApi.useNavigate();
  const [overrideToView, setOverrideToView] = useState<OverridesSummary | undefined>();
  const {
    q = '',
    page = 1,
    productFamilies = [],
    statuses = [],
    overrideReasons = [],
    venueCodes: venues = [],
    viewAllOverridesModalOpen = false,
  } = overrideManagementRouteApi.useSearch();

  const {
    data: venuesData,
    isLoading: isVenuesLoading,
    error: venuesError,
  } = useVenuesQuery({ countryCodes });

  const filteringVenueIds = useMemo(() => {
    return venuesData?.items.filter((venue) => venues.includes(venue.code)).map((v) => v.id);
  }, [venues, venuesData]);

  const { data, isLoading, error } = useListOverridesQuery(
    {
      countryCodes,
      q,
      page,
      productFamilies,
      statuses,
      overrideReasons,
      venueIds: filteringVenueIds,
    },
    true,
  );

  const handleSearch = (value: string) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        q: value.length >= 3 ? value : undefined,
        page: undefined,
      }),
    });
  };

  const onClickViewAll = (overridesSummary: OverridesSummary) => {
    void navigate({
      search: (prev) => ({ ...prev, viewAllOverridesModalOpen: true }),
    });
    setOverrideToView(overridesSummary);
  };

  const onCloseViewAll = () => {
    void navigate({
      search: (prev) => ({ ...prev, viewAllOverridesModalOpen: undefined }),
    });
    setOverrideToView(undefined);
  };

  //Adding temporary ids to data items, as those are aggregated infos and these items as such don't exist in db
  const dataWithTemporaryIds = useMemo(() => {
    return (
      (data &&
        data?.items.map((item) => {
          return { id: uuidv4(), ...item };
        })) ??
      []
    );
  }, [data]);

  const getTotalsDisplay = () => {
    const currentPage = data?.meta.currentPage ?? 0;
    const pageSize = data?.meta.pageSize ?? 0;
    const totalItems = data?.meta.totalItems ?? 0;

    const startIndex = (currentPage - 1) * pageSize + 1;
    const endIndex = Math.min(page * pageSize, totalItems);
    return format('forecasting.showing_n_overrides', {
      start: startIndex,
      end: endIndex,
      totalItems,
    });
  };

  return (
    <>
      <ApiErrorNotification error={error} />
      <Controls direction="row">
        <OverridesFilter
          venues={venuesData ? venuesData.items : []}
          isVenuesLoading={isVenuesLoading}
          venuesError={venuesError}
        />
        <RightContainer>
          <StyledSearchInput
            onChange={(value) => handleSearch(value)}
            onClear={() =>
              void navigate({
                search: (prev) => ({ ...prev, q: undefined, page: undefined }),
              })
            }
            value={q}
            placeholder={format('forecasting.search_items')}
          />
        </RightContainer>
      </Controls>
      <DataTable
        isLoading={isLoading}
        isStickyHeader
        getRowMenu={(record: OverridesSummary) => [
          {
            label: format('forecasting.view_product_overrides'),
            variant: 'black',
            onClick: () => onClickViewAll(record),
            icon: icons.eyeFilled,
          },
        ]}
        emptyMessage={{ title: format('forecasting.no_overrides') }}
        dataSource={dataWithTemporaryIds}
        columns={[
          {
            id: 'product',
            width: 434,
            header: getTotalsDisplay(),
            render: ({ record: { productName = [], gtin, productCode, productId, image } }) => {
              // If product data is null, this is a venue level override
              if (productName === null || productId === null) {
                return (
                  <Stack direction="row" alignItems="center" gap="regularMedium">
                    <AllProductsThumbnail>
                      <Icon source={icons.store} size="small" fill={color.textSubdued} />
                    </AllProductsThumbnail>
                    {format('forecasting.all_products')}
                  </Stack>
                );
              }

              const productNameTranslation =
                productName.find((name) => name.lang === locale) ??
                productName.find((name) => name.lang === countryDefaultLang);

              const languageCode = productNameTranslation?.lang ?? countryDefaultLang;

              return (
                <ProductCard
                  to={
                    languageCode
                      ? `/catalog/$productId/country/$countryCode/lang/$languageCode`
                      : `/catalog/$productId/country/$countryCode`
                  }
                  params={{ countryCode: countryCodes.at(0), productId, languageCode }}
                  name={productNameTranslation?.value}
                  gtin={gtin}
                  productCode={productCode}
                  imgSrc={image?.url}
                />
              );
            },
          },
          {
            id: 'venue',
            header: format('common.venue'),
            render: ({ record }) => {
              return record.venueCode;
            },
          },
          {
            id: 'status',
            header: format('common.status'),
            render: ({ record }) => {
              const impacting = getImpactingOverrides(record.overrides);
              const upcoming = getUpcomingOverrides(record.overrides);
              const ended = getEndedOverrides(record.overrides);

              return (
                <Popover
                  placement="top"
                  content={
                    <PopoverContent>
                      <Stack gap="regularMedium">
                        {impacting.length > 0 && (
                          <Stack gap="narrowSmall">
                            <T.footnoteLabel>
                              {format('forecasting.impacting_periods', { count: impacting.length })}
                            </T.footnoteLabel>
                            <StatusPopoverContentSection overrides={impacting} />
                          </Stack>
                        )}
                        {upcoming.length > 0 && (
                          <Stack gap="narrowSmall">
                            <T.footnoteLabel>{format('forecasting.upcoming')}</T.footnoteLabel>
                            <StatusPopoverContentSection overrides={upcoming} />
                          </Stack>
                        )}
                        {ended.length > 0 && (
                          <Stack gap="narrowSmall">
                            <T.footnoteLabel>{format('forecasting.last_ended')}</T.footnoteLabel>
                            <StatusPopoverContentSection overrides={ended} />
                          </Stack>
                        )}
                        {record.status !== 'ENDED' && (
                          <TextButton onClick={() => onClickViewAll(record)}>
                            {format('forecasting.view_all')}
                          </TextButton>
                        )}
                      </Stack>
                    </PopoverContent>
                  }
                  openOnHover
                >
                  {({ refProps }) => (
                    <Tag {...refProps} variant={getTagVariant(record.status)}>
                      {format(`forecasting.status:${record.status}`)}
                    </Tag>
                  )}
                </Popover>
              );
            },
          },
          {
            id: 'overrideName',
            header: format('forecasting.override_name'),
            render: ({ record }) => {
              const overrideNames = record.overrides.map((period) => {
                return period.overrideName;
              });
              const lastIndex = overrideNames.length - 1;

              return overrideNames[lastIndex];
            },
          },
          {
            id: 'overrideTypeAndReason',
            header: format('forecasting.override_type_and_reason'),
            render: ({ record }) => {
              return (
                <Popover
                  placement="top"
                  content={
                    <PopoverContent>
                      <T.footnote>
                        {format(`common.type`)}:{' '}
                        {format(`forecasting.override_type:${record.overrideType}`)}
                      </T.footnote>
                    </PopoverContent>
                  }
                  openOnHover
                >
                  {({ refProps }) => (
                    <Tag
                      {...refProps}
                      variant="outlineBlack"
                      icon={getTagIcon(record.overrideType)}
                    >
                      {format(`forecasting.reason_code:${record.reasonCode}`)}
                    </Tag>
                  )}
                </Popover>
              );
            },
          },
        ]}
      />
      <Pagination
        activePage={data?.meta.currentPage ?? 0}
        pages={data?.meta.totalPages ?? 0}
        onPageChange={(value) =>
          void navigate({
            search: (prev) => ({ ...prev, page: value }),
          })
        }
      />
      <OverrideSummaryDetailsModal
        onClose={onCloseViewAll}
        overrideToView={overrideToView}
        isOpen={viewAllOverridesModalOpen}
      />
    </>
  );
}
